import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ProductFilterOptionValueCategoryEnum,
  ProductFilterOptionValueSorterEnum,
  ProductFilterOptionValueStatusEnum,
} from '../../constants/filter/productFilter.constant';
import { filterSlice } from '../../store/filterSlice';
import { useAppDispatch, useAppSelector } from '../store/useStore';

// PLP 진입 시 달고 들어온 Search Param 을 필터 옵션에 반영하기 위한 hooks
export const useUpdateProductFilterStates = () => {
  const dispatch = useAppDispatch();
  const productFilterStates = useAppSelector((state) => state.filter.productFilter);
  const filterActions = filterSlice.actions;

  const [searchParams] = useSearchParams();

  const sortSearchParam = useMemo<string>(() => {
    if (searchParams.has('sort')) {
      return searchParams.get('sort')!;
    }
    return '';
  }, [searchParams]);

  const categorySearchParam = useMemo<string>(() => {
    if (searchParams.has('category')) {
      return searchParams.get('category')!;
    }
    return '';
  }, [searchParams]);

  const statusSearchParam = useMemo<string>(() => {
    if (searchParams.has('status')) {
      return searchParams.get('status')!;
    }
    return '';
  }, [searchParams]);

  const brandSearchParam = useMemo<string>(() => {
    if (searchParams.has('brand')) {
      return searchParams.get('brand')!;
    }
    return '';
  }, [searchParams]);

  useEffect(() => {
    // search param 체크가 아직 안되었고, 필터 옵션에 brands가 모두 추가되었을 때 수행
    if (!productFilterStates.isSearchParamChecked && productFilterStates.isFilterOptionsAdded.brands) {
      dispatch(filterActions.resetAllFilters({ filterType: 'productFilter' }));

      // searchParams 이 있으면 수행
      if (searchParams) {
        // sortSearchParam 이 있는 경우
        if (sortSearchParam) {
          Object.values(ProductFilterOptionValueSorterEnum).forEach((sortOptionValue) => {
            if (sortSearchParam.includes(sortOptionValue)) {
              const targetFilter = productFilterStates.globalFilters.find((filter) => filter.key === 'sorter');
              const targetOption = targetFilter?.options.find((option) => option.value === sortOptionValue);

              if (targetFilter && targetOption) {
                dispatch(
                  filterActions.changeFilterOptionIsSelected({
                    filterType: 'productFilter',
                    targetFilter,
                    targetOption,
                  }),
                );
              }
            }
          });
        }

        // categorySearchParam 이 있는 경우
        if (categorySearchParam) {
          Object.values(ProductFilterOptionValueCategoryEnum).forEach((categoryOptionValue) => {
            if (categorySearchParam.includes(categoryOptionValue)) {
              const targetFilter = productFilterStates.globalFilters.find((filter) => filter.key === 'filter_category');
              const targetOption = targetFilter?.options.find((option) => option.value === categoryOptionValue);

              if (targetFilter && targetOption) {
                dispatch(
                  filterActions.changeFilterOptionIsSelected({
                    filterType: 'productFilter',
                    targetFilter,
                    targetOption,
                  }),
                );
              }
            }
          });
        }

        // statusSearchParam 이 있는 경우
        if (statusSearchParam) {
          Object.values(ProductFilterOptionValueStatusEnum).forEach((statusOptionValue) => {
            if (statusSearchParam.includes(statusOptionValue as string)) {
              const targetFilter = productFilterStates.globalFilters.find((filter) => filter.key === 'filter_status');
              const targetOption = targetFilter?.options.find((option) => option.value === statusOptionValue);

              if (targetFilter && targetOption) {
                dispatch(
                  filterActions.changeFilterOptionIsSelected({
                    filterType: 'productFilter',
                    targetFilter,
                    targetOption,
                  }),
                );
              }
            }
          });
        }

        // brandSearchParam 이 있는 경우
        if (brandSearchParam) {
          brandSearchParam.split(',').forEach((brandOptionValue) => {
            if (brandSearchParam.includes(brandOptionValue)) {
              const targetFilter = productFilterStates.globalFilters.find((filter) => filter.key === 'filter_brand');
              const targetOption = targetFilter?.options.find((option) => option.value === brandOptionValue);

              if (targetFilter && targetOption) {
                dispatch(
                  filterActions.changeFilterOptionIsSelected({
                    filterType: 'productFilter',
                    targetFilter,
                    targetOption,
                  }),
                );
              }
            }
          });
        }
      }

      dispatch(filterActions.changeIsSearchParamChecked({ filterType: 'productFilter', value: true }));
    }
  }, [
    dispatch,
    filterActions,
    productFilterStates.globalFilters,
    productFilterStates.isSearchParamChecked,
    productFilterStates.isFilterOptionsAdded.brands,
    searchParams,
    brandSearchParam,
    categorySearchParam,
    sortSearchParam,
    statusSearchParam,
  ]);
};
